import { useEffect, useState } from "react";
import { Plus, KeyRound, XCircle, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { fdate } from "@/lib/format";

export default function Assignments() {
  const [rows, setRows] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/assignments").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const available = vehicles.filter((v) => !["affecte", "restitue"].includes(v.status));

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/assignments", { ...form, mileage: form.mileage ? Number(form.mileage) : undefined });
      toast.success("Affectation créée");
      setOpen(false); setForm({}); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const endAssignment = async (a) => {
    try { await api.post(`/assignments/${a.id}/end`, {}); toast.success("Affectation clôturée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/export/affectations?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `affectations.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Affectations" subtitle="Attribution des véhicules aux collaborateurs.">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="assignment-export-btn"><Download className="mr-2 h-4 w-4" />Exporter</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => doExport("xlsx")}><FileSpreadsheet className="mr-2 h-4 w-4" />Excel (.xlsx)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("csv")}><FileSpreadsheet className="mr-2 h-4 w-4" />CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button onClick={() => { setForm({}); setOpen(true); }} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="assignment-add-btn"><Plus className="mr-2 h-4 w-4" />Nouvelle affectation</Button>
      </PageHeader>

      {rows === null ? <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
        : rows.length === 0 ? <EmptyState icon={KeyRound} title="Aucune affectation" description="Affectez un véhicule à un collaborateur." />
        : (
          <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="border-b border-[#E2E8F0] bg-[#F5F7FA] text-left text-xs uppercase tracking-wide text-[#4A5568]">
                {["Véhicule", "Conducteur", "Début", "Fin", "Site", "Service", "Statut", "Actions"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>{rows.map((a) => (
                <tr key={a.id} className="border-b border-[#EDF0F5] last:border-0 hover:bg-[#F5F7FA]/60" data-testid="assignment-row">
                  <td className="px-4 py-3 font-medium">{a.vehicle_label}</td>
                  <td className="px-4 py-3">{a.employee_label}</td>
                  <td className="px-4 py-3">{fdate(a.start_date)}</td>
                  <td className="px-4 py-3">{a.end_date ? fdate(a.end_date) : "—"}</td>
                  <td className="px-4 py-3">{a.site || "—"}</td>
                  <td className="px-4 py-3">{a.service || "—"}</td>
                  <td className="px-4 py-3">{a.active ? <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Active</span> : <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">Clôturée</span>}</td>
                  <td className="px-4 py-3">{a.active && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline" data-testid="assignment-end-btn"><XCircle className="h-4 w-4" />Clôturer</button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Clôturer l'affectation ?</AlertDialogTitle><AlertDialogDescription>Le véhicule redeviendra disponible.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => endAssignment(a)} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="assignment-confirm-end">Confirmer</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}</td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Nouvelle affectation</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="mb-1.5 block text-sm">Véhicule (disponible)</Label>
              <Select value={form.vehicle_id || ""} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}><SelectTrigger data-testid="assignment-vehicle"><SelectValue placeholder="Sélectionner un véhicule" /></SelectTrigger>
                <SelectContent>{available.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.registration})</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-1.5 block text-sm">Conducteur</Label>
              <Select value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })}><SelectTrigger data-testid="assignment-employee"><SelectValue placeholder="Sélectionner un conducteur" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-sm">Date d'affectation</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="assignment-date" /></div>
              <div><Label className="mb-1.5 block text-sm">Kilométrage</Label><Input type="number" value={form.mileage || ""} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid="assignment-mileage" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-sm">Site</Label><Input value={form.site || ""} onChange={(e) => setForm({ ...form, site: e.target.value })} data-testid="assignment-site" /></div>
              <div><Label className="mb-1.5 block text-sm">Service</Label><Input value={form.service || ""} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="assignment-service" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={create} disabled={saving || !form.vehicle_id || !form.employee_id} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="assignment-save-btn">{saving ? "…" : "Affecter"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
